import {
    BorderLinearProgress,
    ScheduledBadgeWrap,
    StyledButton,
    StyledContent,
    StyledDescription,
    StyledExploreAndActionsWrap,
    StyledExploreButtonWrap,
    StyledInfo,
    StyledInfoWrap,
    StyledLink,
    StyledMediaCard,
    StyledMediaInfoBar,
    StyledName,
    StyledProgressWrapper,
    StyledThumbWrap,
} from "./styled-components";
import { Badge, SimpleThumbnail, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import {
    getDuration,
    getIsLive,
    HtmlReactParser,
    stripTags,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { ButtonAnalyticsProvider, useButtonAnalytics, useEntryUrl } from "@kaltura/mediaspace-shared-hooks";
import {
    ButtonClickAnalyticsType,
    KmsTypeSchedulingTimeInterval,
    MyMediaEntryResultType,
} from "@kaltura/mediaspace-shared-types";
import { Button } from "@kaltura/ds-react-components";
import { memo, ReactNode, useContext, useEffect, useRef } from "react";
import { Actions } from "./actions/Actions";
import MediaInfo from "./media-info/MediaInfo";
import { Schedule } from "@kaltura/mediaspace-shared-media-card";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import { MediaCardContext } from "./media-card-context";

export interface MediaCardProps {
    /**
     * media data as it comes from the server
     */
    media: MyMediaEntryResultType;

    /**
     * active mode. used in media lists. see `onMore` below.
     */
    active?: boolean;

    /**
     * callback for "more" / search results items click
     */
    onMore?: (media: MyMediaEntryResultType) => void;

    /**
     * callback for getting the page to reload the media list
     */
    reloadMediaList?: () => void;

    /**
     * should the card be focused after sidebar closing
     */
    focused: boolean;

    /**
     * do we have full space or only partial
     */
    isWideLayout: boolean;

    /**
     * optional top-left overlay node, use this to add checkbox in the top left corner
     */
    topLeftOverlay?: ReactNode;

    /**
     * Function to update the cached results
     */
    updateCachedResults?: <T>(updater: (oldData: T) => T) => void;

    /**
     * Progress bar value (0-100)
     */
    progressValue?: number;

    /**
     * Progress bar color
     */
    progressColor?: string;
}

const baseClassName = "kms-ds-my-media-card";

/**
 * a card component to display media entry data
 */
const MediaCardComponent = (props: MediaCardProps) => {
    const {
        categoryId,
        openLinksInNewTab,
        displayActionsInline = false,
        showThumbnail = true,
        disableLinks = false,
    } = useContext(MediaCardContext);
    const {
        media,
        active = false,
        onMore,
        reloadMediaList,
        focused,
        isWideLayout,
        topLeftOverlay,
        updateCachedResults,
        progressValue,
        progressColor,
    } = props;
    const { object: baseEntry, schedulingData } = media;

    const elementRef = useRef<HTMLButtonElement | null>(null);

    // cast entry type to allow "schedulingData" which doesn't exist on base-entry
    const entry: KalturaBaseEntry & { schedulingData?: KmsTypeSchedulingTimeInterval } = {
        ...baseEntry,
        ...(schedulingData !== undefined && { schedulingData }),
    } as KalturaBaseEntry & { schedulingData?: KmsTypeSchedulingTimeInterval };

    const entryUrl = useEntryUrl({ entry, categoryId });

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
    const isXsScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const width = isSmallScreen ? 158 : 186;
    const height = isSmallScreen ? 90 : 106;

    const sendButtonAnalytics = useButtonAnalytics();
    const sendAnalytics = (name: string, type: ButtonClickAnalyticsType) => {
        sendButtonAnalytics(name, type, entry.id, undefined, "Media Card");
    };

    const onExplore = () => {
        sendAnalytics("card - Explore", ButtonClickAnalyticsType.EXPAND);
        onMore?.(media);
    };

    useEffect(() => {
        if (focused) {
            // using requestAnimationFrame to avoid potential layout thrashing
            // and ensure the focus happens at the right time in the rendering cycle.
            const handle = requestAnimationFrame(() => {
                // focus 'explore' button when closing explore sidebar
                elementRef.current?.focus();
            });
            return () => cancelAnimationFrame(handle);
        }
    }, [focused]);

    const entryName = (
        <TruncateWithTooltip text={entry.name} lines={isXsScreen ? 2 : 1}>
            <StyledName component="h2" variant={isSmallScreen ? "body1Highlight" : "h5"}>
                {entry.name}
            </StyledName>
        </TruncateWithTooltip>
    );

    const thumbnail = (
        <SimpleThumbnail
            thumbnailUrl={`${entry.thumbnailUrl}/width/${width}/height/${height}/type/3`}
            name={entry.name}
            duration={getDuration(entry)}
            isLive={getIsLive(entry)}
        />
    );

    return (
        <StyledMediaCard className={`${baseClassName}`} active={active}>
            <ButtonAnalyticsProvider location={"Media Card"}>
                {topLeftOverlay}
                {showThumbnail && (
                    <StyledThumbWrap className={`${baseClassName}-thumbnail`}>
                        <StyledProgressWrapper>
                            {disableLinks ? (
                                thumbnail
                            ) : (
                                <StyledLink
                                    href={entryUrl}
                                    target={openLinksInNewTab ? "_blank" : undefined}
                                    className={`${baseClassName}-thumbnail-link`}
                                >
                                    {thumbnail}
                                </StyledLink>
                            )}
                            {progressValue !== undefined && progressValue > 0 && (
                                <BorderLinearProgress
                                    variant="determinate"
                                    value={progressValue}
                                    progresscolor={progressColor}
                                />
                            )}
                        </StyledProgressWrapper>
                    </StyledThumbWrap>
                )}
                <StyledContent className={`${baseClassName}-info`}>
                    <StyledInfoWrap>
                        <StyledInfo>
                            {disableLinks ? (
                                entryName
                            ) : (
                                <StyledLink
                                    href={entryUrl}
                                    target={openLinksInNewTab ? "_blank" : undefined}
                                    onClick={() => {
                                        sendAnalytics("card - Media name link", ButtonClickAnalyticsType.LINK);
                                    }}
                                    className={`${baseClassName}-info-name`}
                                >
                                    {entryName}
                                </StyledLink>
                            )}
                            {!!media.schedulingData && !isXsScreen && (
                                <Schedule
                                    schedulingData={media.schedulingData}
                                    className={`${baseClassName}-info-schedule`}
                                />
                            )}
                            {!!schedulingData && isXsScreen && (
                                <ScheduledBadgeWrap>
                                    <Badge label={translate("scheduled")} size={"small"} mode={"light"} />
                                </ScheduledBadgeWrap>
                            )}
                            {!isSmallScreen && (
                                <>
                                    <StyledDescription
                                        lines={schedulingData ? 1 : 2}
                                        showMore={false}
                                        className={`${baseClassName}-info-description`}
                                    >
                                        {/* we use HtmlReactParser here to decode html entities */}
                                        {HtmlReactParser(stripTags(entry.description))}
                                    </StyledDescription>
                                    <MediaInfo media={media} showFullData={isWideLayout || !displayActionsInline} />
                                </>
                            )}
                        </StyledInfo>
                        {isSmallScreen && media.actions && (
                            <Actions
                                menuItems={media.actions}
                                entryId={entry.id}
                                entryName={entry.name}
                                displayInline={displayActionsInline}
                                reloadMediaList={reloadMediaList}
                                updateCachedResults={updateCachedResults}
                            />
                        )}
                    </StyledInfoWrap>
                    {isSmallScreen && (
                        <StyledMediaInfoBar>
                            {!isXsScreen && (
                                <MediaInfo media={media} showFullData={isWideLayout || !displayActionsInline} />
                            )}
                            <StyledButton
                                color={"secondary"}
                                variant={"borderless"}
                                onClick={onExplore}
                                className={`${baseClassName}-explore-button`}
                                ref={elementRef}
                            >
                                {translate("Explore")}
                            </StyledButton>
                        </StyledMediaInfoBar>
                    )}
                </StyledContent>
                {!isSmallScreen && (
                    <StyledExploreAndActionsWrap>
                        {media.actions && (
                            <Actions
                                menuItems={media.actions}
                                entryId={entry.id}
                                entryName={entry.name}
                                displayInline={displayActionsInline}
                                reloadMediaList={reloadMediaList}
                                updateCachedResults={updateCachedResults}
                            />
                        )}
                        <StyledExploreButtonWrap>
                            <Button
                                variant={"borderless"}
                                color={"secondary"}
                                onClick={onExplore}
                                className={`${baseClassName}-explore-button`}
                                aria-label={translate("Explore %1", [entry.name])}
                                ref={elementRef}
                            >
                                {translate("Explore")}
                            </Button>
                        </StyledExploreButtonWrap>
                    </StyledExploreAndActionsWrap>
                )}
            </ButtonAnalyticsProvider>
        </StyledMediaCard>
    );
};

// Memoize MediaCard to prevent unnecessary re-renders when props haven't changed
export const MediaCard = memo(
    MediaCardComponent,
    (prevProps, nextProps) => {
        // Custom comparison function - only re-render if these specific props change
        return (
            prevProps.media.object.id === nextProps.media.object.id
            && prevProps.active === nextProps.active
            && prevProps.focused === nextProps.focused
            && prevProps.isWideLayout === nextProps.isWideLayout
            && prevProps.topLeftOverlay === nextProps.topLeftOverlay
            && prevProps.progressValue === nextProps.progressValue
            && prevProps.progressColor === nextProps.progressColor
        );
    }
);

export default MediaCard;
